import * as _ from "lodash";
import * as React from "react";
import styled from "styled-components";
import * as S from "@styled";

import { Utilities } from "../../shared/utilities";
import { AppContext } from "../App";
import { IAppReducerState, IRuleData } from "../shared/model";

import { Permissions } from "../../shared/model/AllPermissions";
import { HashMap } from "../../shared/model/HashMap";
import { getRules } from "../actions/AppActions";
import RuleEditor from "./RuleEditor";
import RuleRowChart from "./RuleRowChart";
import { useParams } from "react-router-dom";
import { DisplayName } from "shared/components/DisplayName";
import { ChangeEvent } from "shared/model/Event";
import theme from "styled-theming";
import { ThemeType } from "shared/styles/theme";

const indentedValueColor = theme("mode", {
    [ThemeType.light]: S.Colors.black,
    [ThemeType.dark]: S.Colors.grey_3
})

const RuleContainer: React.FC = () => {

    const { state } = React.useContext<IAppReducerState>(AppContext);

    const selectedRule = state.rules?.find((rule) => rule.id === state.selectedRuleId);

    if (selectedRule == null && !state.showNewRuleEditor) {
        return null;
    }

    return (
        <S.Panel>
            <S.PanelBody>
                <S.PanelPadding>
                    {selectedRule != null &&
                        <RuleView key={selectedRule.id} rule={selectedRule} />
                    }

                    {!selectedRule && state.showNewRuleEditor && (
                        <RuleEditor asNewRule={true} />
                    )}
                </S.PanelPadding>
            </S.PanelBody>
        </S.Panel>
    );
};

const RuleAcknowledgeAlertButton: React.FC<{ onClick: () => void }> = ({ onClick }) => {
    return (
        <S.Button onClick={onClick}>
            <S.ButtonIcon className="ci-check" />
            {FRENDS.Localizations.AcknowledgeAlerts}
        </S.Button>
    );
};

const StyledRuleDeactivationButton = styled(S.Button)`
    padding: 0 25px 0 15px;
`;

interface RuleViewButton {
    rule: IRuleData;
    onClick: () => void;
}

const RuleDeactivationButton: React.FC<RuleViewButton> = ({ rule, onClick }) => {
    return (
        <StyledRuleDeactivationButton
            id={`toggle-deactivation-rule-${rule.id}`}
            className={rule.isDeactivated ? 'deactivation-rule-on' : 'deactivation-rule-off'}
            onClick={onClick}
        >
            {rule.isDeactivated ? (
                <>
                    <S.ButtonIcon className="ci-play_arrow" />
                    {FRENDS.Localizations.Activate}
                </>
            ) : (
                <>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <rect x="8" y="5" width="2" height="14" fill="currentColor" />
                        <rect x="13" y="5" width="2" height="14" fill="currentColor" />
                    </svg>
                    {FRENDS.Localizations.Deactivate}
                </>
            )}
        </StyledRuleDeactivationButton>
    );
};

const StyledRuleRemoveButton = styled(S.Button)`
    padding-left: 15px;
    padding-right: 15px;

    i {
        font-size: var(--header-2-font-size);
    }
`;

const RuleRemoveButton: React.FC<RuleViewButton> = ({ rule, onClick }) => {
    return (
        <StyledRuleRemoveButton onClick={onClick} id={`delete-rule-${rule.id}`}>
            <i className="ci-trash_full" />
        </StyledRuleRemoveButton>
    );
};

const StyledRuleLabelWithIndentedValue = styled(S.LabelWithIndentedValue)`
    div.value {
        color: ${indentedValueColor};
        font-size: 1rem;
    }
`;

export const RuleView: React.FC<{ rule: IRuleData }> = ({ rule }) => {

    const { state, dispatch } = React.useContext<IAppReducerState>(AppContext);
    const { environmentName } = useParams<{ environmentName: string }>();
    const [showEditRule, setShowEditRule] = React.useState<boolean>(false);

    const [name, setName] = React.useState<string>(rule.name)
    const [isEditingName, setIsEditingName] = React.useState<boolean>(false);
    const [hasNameError, setHasNameError] = React.useState<boolean>(false);

    const updateRuleName = async () => {
        dispatch({ type: "SET_IS_LOADING", isLoading: true });
        setIsEditingName(false);
        try {
            await Utilities.patchJsonAndParseResponse(`api/monitoringRules/editRuleName/${rule.id}`, { newName: name });
            Utilities.toastSuccess("Rule name updated.")
            getRules(dispatch, environmentName);
        } catch (e) {
            console.error(e);
            Utilities.toastError(e);
        }
        dispatch({ type: "SET_SAVE_BUTTON_CLICKED", saveButtonClicked: false });
        dispatch({ type: "SET_IS_DIRTY", isDirty: false });
        dispatch({ type: "SET_IS_LOADING", isLoading: false });
    }

    React.useEffect(() => {
        if (state.saveButtonClicked) {
            updateRuleName();
        }
    }, [state.saveButtonClicked])


    const weekDays = [
        {
            name: FRENDS.Localizations.Monday,
            value: "MON"
        },
        {
            name: FRENDS.Localizations.Tuesday,
            value: "TUE"
        },
        {
            name: FRENDS.Localizations.Wednesday,
            value: "WED"
        },
        {
            name: FRENDS.Localizations.Thursday,
            value: "THU"
        },
        {
            name: FRENDS.Localizations.Friday,
            value: "FRI"
        },
        {
            name: FRENDS.Localizations.Saturday,
            value: "SAT"
        },
        {
            name: FRENDS.Localizations.Sunday,
            value: "SUN"
        }
    ];

    const handleEditRule = () => {
        if (showEditRule) {
            dispatch({ type: "SET_IS_DIRTY", isDirty: false });
        }
        setShowEditRule(!showEditRule);
    };

    const handleRemoveRule = async () => {
        const shouldRemoveRule = confirm("Are you sure you would like to delete this rule?");

        if (shouldRemoveRule) {
            dispatch({ type: "SET_IS_LOADING", isLoading: true });
            try {
                await Utilities.deleteFetchAndParseResponse(`api/monitoringRules/removeRule/${rule.id}`);
                Utilities.toastSuccess("Rule was successfully removed.");
                getRules(dispatch, environmentName);
            } catch (e) {
                Utilities.toastError(e);
                console.error(e);
            }
            dispatch({ type: "SET_IS_LOADING", isLoading: false });
        }
    };

    const handleAcknowledgeAlert = async () => {
        dispatch({ type: "SET_IS_LOADING", isLoading: true });
        try {
            await Utilities.postJsonAndParseResponse(`api/monitoringRules/acknowledgeAlerts/${rule.id}`, {});
            Utilities.toastSuccess(`Acknowledged alerts for ${rule.name}.`);
            getRules(dispatch, environmentName);
        } catch (e) {
            Utilities.toastError(e);
            console.error(e);
        }
        dispatch({ type: "SET_IS_LOADING", isLoading: false });
    };

    const handleToggleDeactivation = async () => {
        dispatch({ type: "SET_IS_LOADING", isLoading: true });
        try {
            const toggled = await Utilities.patchJsonAndParseResponse<string>(`api/monitoringRules/toggleDeactivation/${rule.id}`, {});
            Utilities.toastSuccess(`${toggled} ${rule.name}.`);
            getRules(dispatch, environmentName);
        } catch (e) {
            Utilities.toastError(e);
            console.error(e);
        }
        dispatch({ type: "SET_IS_LOADING", isLoading: false });
    };

    const getTimeZoneDescription = (timeZoneId: string) => {
        const tzInfo = _.find(state?.timeZones, (tz) => tz.id === timeZoneId);
        return tzInfo && tzInfo.description;
    };

    const localizeText = (s: string): string => {
        if (s) {
            return (FRENDS.Localizations as unknown as HashMap<string>)[s] || s;
        } else {
            return s;
        }
    };

    const handleSetName = (e: ChangeEvent<HTMLInputElement>) => {
        if (state.rules?.some((s) => s.name === e.target.value)) {
            setHasNameError(true);
            dispatch({ type: "SET_IS_DIRTY", isDirty: false });
        } else {
            setHasNameError(false);
            dispatch({ type: "SET_IS_DIRTY", isDirty: true });
        }
        setName(e.target.value);
    }

    return (
        <>
            <S.PanelHeading>
                {isEditingName ? (
                    <S.FormGroup variant={hasNameError ? S.FormGroupVariant.error : S.FormGroupVariant.primary}>
                        <S.Input
                            id="name-input"
                            type="text"
                            name="ruleName"
                            onChange={handleSetName}
                            isFullWidth={true}
                            required={true}
                            value={name}
                        />
                        {hasNameError && <span className="error"></span>}
                    </S.FormGroup>
                ) : (
                    <DisplayName
                        displayName={rule.name}
                        setDisplayName={() => setIsEditingName(true)}
                    />
                )}
                {state.userPermissions[Permissions.MonitoringRulesEdit] && (
                    <S.ButtonGroup gap={6}>
                        {rule.alertsCount > 0 && (
                            <RuleAcknowledgeAlertButton onClick={handleAcknowledgeAlert} />
                        )}
                        <RuleDeactivationButton
                            rule={rule}
                            onClick={handleToggleDeactivation}
                        />
                        <RuleRemoveButton
                            rule={rule}
                            onClick={handleRemoveRule}
                        />
                    </S.ButtonGroup>
                )}
            </S.PanelHeading>
            <StyledRuleLabelWithIndentedValue>
                <S.Label>{FRENDS.Localizations.Rule_VariableToMonitor}</S.Label>
                <div className="value">{rule.config.variableName}</div>
            </StyledRuleLabelWithIndentedValue>
            {rule.config.expressionOperator !== "Exists" && (
                <StyledRuleLabelWithIndentedValue>
                    <S.Label>{FRENDS.Localizations.Rule_FilterPromotedVariableValues}</S.Label>
                    <div className="value">{localizeText(`RuleExpressionOperator_${rule.config.expressionOperator}`).toLowerCase()} {rule.config.expressionExpectedValue}</div>
                </StyledRuleLabelWithIndentedValue>
            )}
            <StyledRuleLabelWithIndentedValue>
                <S.Label>{FRENDS.Localizations.Rule_SendAlertIf}</S.Label>
                <div className="value"> {localizeText(`RuleFunction_${rule.config.aggregate}`)} {localizeText(`RuleOperator_${rule.config.operator}`).toLowerCase()} {rule.config.expectedValue}</div>
            </StyledRuleLabelWithIndentedValue>
            <StyledRuleLabelWithIndentedValue>
                <S.Label>{FRENDS.Localizations.TimeInterval}</S.Label>
                <div className="value">{rule.config.timePeriod} {rule?.config?.timePeriodType?.toLowerCase()}</div>
                <div className="value">{getTimeZoneDescription(rule.config.timeZone)}</div>
                <div className="value">
                    {rule.config.isActiveEveryDay ?
                        FRENDS.Localizations.OnEveryDay
                        : `${FRENDS.Localizations.OnSelectedDays}: ${rule?.config?.activeWeekDays?.map((x) => _.find(weekDays, (d) => d.value === x)?.name).join(", ")}`
                    }
                </div>
                <div className="value">
                    {rule.config.isActiveEntireDay ?
                        FRENDS.Localizations.EntireDay
                        : `${rule.config.startHour}:00 - ${rule.config.endHour}:00`
                    }</div>
            </StyledRuleLabelWithIndentedValue>

            <RuleRowChart rule={rule} />

            <S.FormGroup style={{ marginTop: 16 }}>
                <h3 onClick={handleEditRule} className="pointer rule-settings-selector" style={{ userSelect: "none" }}>
                    {FRENDS.Localizations.Settings}<i style={{ verticalAlign: "bottom", marginLeft: 16 }} className={`${showEditRule ? "ci-chevron_big_up" : "ci-chevron_big_down"}`}></i>
                </h3>
            </S.FormGroup>
            {state.userPermissions[Permissions.MonitoringRulesEdit] && (
                <>
                    {showEditRule && (
                        <RuleEditor
                            asEditRule={true}
                            rule={rule}
                            onHideCallback={handleEditRule}
                        />
                    )}
                </>
            )}
        </>
    );
};

export default RuleContainer;
